import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import fullTime from 'flarum/common/helpers/fullTime';
import EditEventModal from './EditEventModal';
import InterestedList from './InterestedList';

/**
 * Rendered inside an event-tagged discussion via DiscussionPage content ItemList.
 * Shows event metadata + Interested list + Edit Event Details button.
 *
 * Props:
 *   - event: Event model
 *   - discussion: Discussion model
 *   - onEventSaved: callback(updatedEvent) fired after a successful save
 */
export default class EventDiscussionHero extends Component {
	view() {
		const { event, discussion, post, onEventSaved } = this.attrs;

		if (!event) return null;

		const startDate = event.event_start();
		const isScheduled = event.is_scheduled() && startDate && new Date(startDate).getFullYear() > 1970;
		const isDeleted   = event.is_deleted();

		const canEdit = app.session.user && (
			app.session.user.canModerateEvents() ||
			(discussion?.user && discussion.user()?.id() === app.session.user.id())
		);

		// Find the first post from the post stream state
		const firstPost = post || this.getFirstPost(discussion);

		// Google Calendar link
		let gcalLink = null;
		if (isScheduled) {
			const fmt = (d) => new Date(d).toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';
			const params = new URLSearchParams({
				action:   'TEMPLATE',
				text:     discussion?.title() || '',
				dates:    `${fmt(event.event_start())}/${fmt(event.event_end())}`,
				location: event.location() || '',
				details:  window.location.href,
			});
			gcalLink = `https://calendar.google.com/calendar/render?${params}`;
		}

		return (
			<div className={`EventHero${isDeleted ? ' EventHero--deleted' : ''}`}>

			{isDeleted && (
				<div className="EventHero-deleted">
				{app.translator.trans('kemze-calendar.forum.event.deleted_notice')}
				</div>
			)}

			<div className="EventHero-meta">
			{isScheduled ? (
				<div className="EventHero-times">
				<i className="fas fa-calendar-alt" />
				{' '}
				{fullTime(event.event_start())}
				<span className="EventHero-separator">→</span>
				{fullTime(event.event_end())}
				<span className="EventHero-duration">
				({(event.duration_minutes() / 60).toFixed(1).replace(/\.0$/, '')} {app.translator.trans('kemze-calendar.forum.event.minutes')})
				</span>
				</div>
			) : (
				<div className="EventHero-unscheduled">
				<i className="fas fa-clock" />
				{' '}
				{app.translator.trans('kemze-calendar.forum.event.no_date')}
				{canEdit && (
					<span className="EventHero-unscheduled-cta">
					{' — '}{app.translator.trans('kemze-calendar.forum.event.set_date_cta')}
					</span>
				)}
				</div>
			)}

			{event.location() && (
				<div className="EventHero-location">
				<i className="fas fa-map-marker-alt" />
				{' '}
				{event.location()}
				</div>
			)}

			{event.event_type() && (
				<span
				className="EventHero-type"
				style={`background:${this.getTypeColor(event.event_type())}`}
				>
				{event.event_type()}
				</span>
			)}

			<div className="EventHero-actions">
			{canEdit && (
				<Button
				className="Button Button--default EventHero-editBtn"
				icon="fas fa-edit"
				onclick={() => app.modal.show(EditEventModal, {
					event,
					onSave: (updatedEvent) => {
						if (onEventSaved) onEventSaved(updatedEvent);
					},
				})}
				>
				{app.translator.trans('kemze-calendar.forum.event.edit_details')}
				</Button>
			)}

			{gcalLink && (
				<a
				className="Button Button--default EventHero-gcalBtn"
				href={gcalLink}
				target="_blank"
				rel="noopener noreferrer"
				>
				<i className="fab fa-google" />
				{' '}
				{app.translator.trans('kemze-calendar.forum.event.add_to_gcal')}
				</a>
			)}
			</div>
			</div>

			{firstPost && (
				<div className="EventHero-interested">
				{InterestedList.component({ post: firstPost, event: event })}
				</div>
			)}
			</div>
		);
	}

	getFirstPost(discussion) {
		if (!discussion) return null;
		return app.store.all('posts').find(
			(p) => p.discussion && p.discussion()?.id() === discussion.id() && p.number() === 1
		) || null;
	}

	getTypeColor(typeName) {
		try {
			const raw   = app.forum.attribute('kemze-calendar.event_types') || '[]';
			const types = typeof raw === 'string' ? JSON.parse(raw) : raw;
			const found = types.find((t) => t.name === typeName);
			return found?.color || 'var(--primary-color)';
		} catch {
			return 'var(--primary-color)';
		}
	}
}
